import { useEffect } from "react";

// This is a pure client-side SPA (no SSR/prerendering) — index.html ships one
// static <title>/meta set, so every route showed identical metadata in tabs,
// search results and social shares. This hook rewrites document.title and
// upserts the relevant <meta>/<link> tags on mount so each page gets its own,
// without pulling in react-helmet-async for what's a ~50-line DOM operation.
// Known limitation: since nothing is pre-rendered, a crawler/scraper that
// does NOT execute JavaScript (most social-card scrapers, some bots) will
// still see only the static index.html tags — full coverage there needs
// server-side rendering or prerendering, which is out of scope for this
// codebase. Googlebot itself does execute JS and will see these tags.
interface SEOProps {
  title: string;
  description?: string;
  /** Path only, e.g. "/courses/mountaineering" — joined with the site origin. Defaults to the current URL (without query string). */
  path?: string;
  image?: string;
  type?: string;
  /** Set true for pages that should not be indexed (e.g. auth pages) even though nothing blocks them at the route level. */
  noindex?: boolean;
}

const SITE_NAME = "NIMAS Dirang";
const SITE_URL = "https://nimasdirang.com";
const DEFAULT_IMAGE = "/images/generated/default-seo-banner.png";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSEO({ title, description, path, image, type = "website", noindex = false }: SEOProps) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    document.title = fullTitle;

    const url = path ? `${SITE_URL}${path}` : window.location.origin + window.location.pathname;
    upsertLink("canonical", url);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE_NAME);

    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }

    const img = image || DEFAULT_IMAGE;
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:image", img);

    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
  }, [title, description, path, image, type, noindex]);
}
